"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import { Button, Tooltip } from "@coval/ui"
import { X } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"
import type { RunRowLike } from "@/types/RunRow"

import { useLiveSimulationTranscript } from "../routes/useLiveSimulationTranscript"
import { useSimulationRoomSelection } from "../routes/useSimulationRoomSelection"
import { isSimulationLive } from "../utils/isSimulationLive"
import { isTextBasedSimulator } from "../utils/isTextBasedSimulator"
import { LiveIndicator } from "./LiveIndicator"
import { LiveSimulationView } from "./LiveSimulationView"

interface SimulationRoomPanelControls {
  /** Whether the panel is currently open. */
  isOpen: boolean
  /** Whether any of the given rows is currently open to listeners. */
  hasLiveSimulations: boolean
  /**
   * Open the panel, optionally pointing it at a specific simulation (the
   * row-level entry point). The transcript starts streaming on its own;
   * audio stays muted until the user presses Listen inside the panel.
   */
  openPanel: (simulationId?: string) => void
  closePanel: () => void
  /**
   * Render this where the panel should sit in the layout. It is `null` while
   * closed, so the column it lives in collapses rather than standing empty.
   */
  panelElement: React.ReactNode
}

/**
 * One live-sims panel per page. The page renders `panelElement` as a column of
 * its layout and opens it from any affordance it likes (a row-level LIVE
 * badge). Selection policy stays in `useSimulationRoomSelection`; the room
 * itself only exists while the panel is open, so closing it tears the
 * connection down.
 */
export function useSimulationRoomPanel(
  runRows: RunRowLike[],
): SimulationRoomPanelControls {
  const selection = useSimulationRoomSelection(runRows)
  const [isOpen, setIsOpen] = useState(false)

  // Keep the callbacks stable — row cell renderers are memoized, and the
  // selection object is rebuilt on every render.
  const selectionRef = useRef(selection)
  selectionRef.current = selection

  const openPanel = useCallback((simulationId?: string) => {
    const current = selectionRef.current
    if (simulationId && simulationId !== current.selectedId) {
      // Avoid remounting the current room while the user may be listening.
      current.select(simulationId)
    }
    setIsOpen(true)
  }, [])

  const closePanel = useCallback(() => {
    setIsOpen(false)
    // Closing unmounts the room, so any engagement is over; resetting lets
    // the selection policy move on from a finished simulation.
    selectionRef.current.handleStop()
  }, [])

  return {
    isOpen,
    hasLiveSimulations: selection.listenableIds.length > 0,
    openPanel,
    closePanel,
    // The room only exists while the panel is open: rendering nothing here on
    // close is what unmounts (and tears down) the connection.
    panelElement: isOpen ? (
      <SimulationRoomPanel
        onClose={closePanel}
        runRows={runRows}
        selection={selection}
      />
    ) : null,
  }
}

/**
 * The live simulation in the same transcript container the simulation detail
 * page uses: a titled header over `LiveSimulationView`, so a live sim reads
 * identically wherever it is watched from.
 */
function SimulationRoomPanel({
  onClose,
  runRows,
  selection,
}: {
  onClose: () => void
  runRows: RunRowLike[]
  selection: ReturnType<typeof useSimulationRoomSelection>
}) {
  const router = useRouter()
  const queryParams = useQueryParams()
  const selectedRow = useMemo(
    () =>
      runRows.find(
        (row) => row.simulation_output_id === selection.selectedId,
      ) ?? null,
    [runRows, selection.selectedId],
  )
  const resultHref = selectedRow
    ? queryParams.getHref(
        `/runs/${selectedRow.run_id}/results/${selectedRow.simulation_output_id}`,
        typeof router.query.slug === "string" ? router.query.slug : undefined,
      )
    : null

  return (
    <div className="flex h-full flex-col bg-surface-default">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-surface-2 px-2 py-2">
        <div className="flex min-w-0 items-baseline gap-2 px-2">
          {selectedRow?.test_case_description?.trim() ? (
            <p className="truncate text-sm font-semibold text-coval-text">
              {selectedRow.test_case_description}
            </p>
          ) : (
            <span className="text-sm font-semibold text-coval-text">
              Live Simulation
            </span>
          )}
          {selectedRow && resultHref ? (
            <Link
              href={resultHref}
              className="shrink-0 font-mono text-xs text-coval-text-muted underline-offset-2 hover:underline"
              data-testid="live-simulation-panel-id"
            >
              {selectedRow.simulation_output_id}
            </Link>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center">
          {/* No badge once the call is over — the panel is retained for a
              listener who was following it, and neither LIVE nor "In
              progress" would be true any more. */}
          {selectedRow && isSimulationLive(selectedRow) ? (
            <Tooltip
              content="Live transcript — text and timestamps may not exactly match the final transcript."
              className="max-w-64 text-center"
            >
              <span>
                <LiveIndicator size="sm" />
              </span>
            </Tooltip>
          ) : null}
          <Button
            icon
            variant="ghost"
            tooltip="Close the live simulation panel"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {selectedRow === null ? (
        <div className="flex flex-1 min-h-0 items-center justify-center px-6">
          <p className="text-sm text-coval-text-muted">
            No simulations are live right now.
          </p>
        </div>
      ) : (
        <SimulationRoomPanelRoom
          key={selectedRow.simulation_output_id}
          row={selectedRow}
          isEngaged={selection.isEngaged}
          onEngage={selection.handleStart}
        />
      )}
    </div>
  )
}

function SimulationRoomPanelRoom({
  row,
  isEngaged,
  onEngage,
}: {
  row: RunRowLike
  isEngaged: boolean
  onEngage: () => void
}) {
  const live = useLiveSimulationTranscript(row, {
    startMuted: !isEngaged,
  })
  // Unmuting is the panel's Listen gesture — the moment the user opts into
  // following this simulation, so the selection policy keeps its transcript
  // around after it finishes instead of rotating away.
  const { isMuted, toggleMute } = live
  const engageOnListen = useCallback(() => {
    if (isMuted) {
      onEngage()
    }
    toggleMute()
  }, [isMuted, onEngage, toggleMute])
  const engagedLive = useMemo(
    () => ({ ...live, toggleMute: engageOnListen }),
    [engageOnListen, live],
  )

  const isTextSimulation = isTextBasedSimulator(row.model_type)
  // Text-based rows have no audio, so Listen stays disabled and can never
  // fire the gesture above. Opening the panel on one is the only
  // engagement moment there is — without it, the row finishing before
  // engagement clears the panel instead of keeping the completed
  // transcript in view.
  useEffect(() => {
    if (isTextSimulation && !isEngaged) {
      onEngage()
    }
  }, [isTextSimulation, isEngaged, onEngage])

  return (
    <LiveSimulationView
      live={engagedLive}
      isTextSimulation={isTextSimulation}
    />
  )
}
